import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { auth } from '../firebase/firebase';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword, multiFactor } from 'firebase/auth';
import api from '../api/api';
import { notify } from '../utils/notifications';
import { checkPassword, passwordErrorMessage, PASSWORD_POLICY_MESSAGE } from '../utils/passwordPolicy';
import { PasswordStrengthMeter } from './PasswordStrengthMeter';
import '../styles/UserProfile.css';

/**
 * C2: the user's enrolled second factors, or [] when there is no signed-in
 * user or the SDK object does not support multiFactor (mocked users in tests).
 */
const readEnrolledFactors = () => {
    const u = auth.currentUser;
    if (!u) return [];
    try {
        return multiFactor(u).enrolledFactors ?? [];
    } catch (err) {
        return [];
    }
};

export const UserProfile = () => {
    const { user, userProfile, isAdmin, isFacilityAdmin, mfaEnrolled, refreshMfaState } = useAuth();
    const navigate = useNavigate();

    const [name, setName] = useState(userProfile?.name || '');
    const [organization, setOrganization] = useState(userProfile?.organization || '');
    const [saving, setSaving] = useState(false);

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [changingPassword, setChangingPassword] = useState(false);
    const [passwordError, setPasswordError] = useState('');

    const [facilityConfig, setFacilityConfig] = useState(null);

    const [enrolledFactors, setEnrolledFactors] = useState(readEnrolledFactors);
    const [removingMfa, setRemovingMfa] = useState(false);
    const mfaRequired = isAdmin || isFacilityAdmin;

    // AuthContext re-reads the factors on each token change; follow it so the
    // status stays current after enrollment elsewhere in the session.
    useEffect(() => {
        setEnrolledFactors(readEnrolledFactors());
    }, [mfaEnrolled]);

    const handleRemoveMfa = async () => {
        const u = auth.currentUser;
        const factor = enrolledFactors[0];
        if (!u || !factor) return;
        if (!window.confirm('Remove two-factor authentication from your account?')) return;
        setRemovingMfa(true);
        try {
            await multiFactor(u).unenroll(factor);
        } catch (err) {
            if (err?.code === 'auth/requires-recent-login') {
                notify.error('Sign in again, then remove the factor.');
            } else {
                notify.error('Could not remove two-factor authentication. Try again.');
            }
            setRemovingMfa(false);
            return;
        }
        // The factor is gone from here on; a failed context refresh must not
        // read as a failed removal. AuthContext re-reads on the next token change.
        setEnrolledFactors([]);
        try {
            await refreshMfaState();
        } catch (err) {
            // Fall through to the success toast.
        }
        setRemovingMfa(false);
        notify.success('Two-factor authentication removed.');
    };

    useEffect(() => {
        const facilityType = userProfile?.facility_type;
        if (!facilityType) return;
        api.get(`/admin/facility-configs/${facilityType}`)
            .then((resp) => setFacilityConfig(resp.data))
            .catch(() => setFacilityConfig(null));
    }, [userProfile?.facility_type]);

    const handleSaveProfile = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.put('/user/profile', {
                name: name.trim(),
                organization: organization.trim(),
            });
            // Profile updates reactively via Firestore onSnapshot
            notify.success('Profile updated.');
        } catch (err) {
            notify.error('Failed to update profile.');
        } finally {
            setSaving(false);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setPasswordError('');

        if (newPassword !== confirmNewPassword) {
            setPasswordError('New passwords do not match.');
            return;
        }

        const { ok } = checkPassword(newPassword);
        if (!ok) {
            setPasswordError(PASSWORD_POLICY_MESSAGE);
            return;
        }

        setChangingPassword(true);
        try {
            const user = auth.currentUser;
            const credential = EmailAuthProvider.credential(user.email, currentPassword);
            await reauthenticateWithCredential(user, credential);
            await updatePassword(user, newPassword);

            setCurrentPassword('');
            setNewPassword('');
            setConfirmNewPassword('');
            notify.success('Password changed successfully.');
        } catch (err) {
            const policyMsg = passwordErrorMessage(err);
            if (policyMsg) {
                setPasswordError(policyMsg);
            } else if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
                setPasswordError('Current password is incorrect.');
            } else {
                setPasswordError('Failed to change password. Please try again.');
            }
        } finally {
            setChangingPassword(false);
        }
    };

    return (
        <div className="profile-container">
            <h2>User Profile</h2>

            <div className="profile-section">
                <h3>Personal Information</h3>
                <form onSubmit={handleSaveProfile}>
                    <div className="profile-field">
                        <label htmlFor="profileName">Name:</label>
                        <input
                            id="profileName"
                            type="text"
                            className="profile-input"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                        />
                    </div>
                    <div className="profile-field">
                        <label htmlFor="profileEmail">Email:</label>
                        <input
                            id="profileEmail"
                            type="email"
                            className="profile-input"
                            value={user?.email || ''}
                            disabled
                        />
                    </div>
                    <div className="profile-field">
                        <label htmlFor="profileOrg">Organization:</label>
                        <input
                            id="profileOrg"
                            type="text"
                            className="profile-input"
                            value={organization}
                            onChange={(e) => setOrganization(e.target.value)}
                        />
                    </div>
                    <button type="submit" disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                </form>

                {userProfile?.facility_type && (
                    <div className="profile-facility">
                        <label>Facility Workflow:</label>
                        <div className="profile-facility-info">
                            <span className="profile-facility-name">
                                {facilityConfig?.name || userProfile.facility_type}
                            </span>
                            {facilityConfig?.description && (
                                <span className="profile-facility-desc">{facilityConfig.description}</span>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <div className="profile-section">
                <h3>Change Password</h3>
                <form onSubmit={handleChangePassword}>
                    {passwordError && <div className="error">{passwordError}</div>}
                    <div className="profile-field">
                        <label htmlFor="currentPwd">Current Password:</label>
                        <input
                            id="currentPwd"
                            type="password"
                            className="profile-input"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            required
                            autoComplete="current-password"
                        />
                    </div>
                    <div className="profile-field">
                        <label htmlFor="newPwd">New Password:</label>
                        <input
                            id="newPwd"
                            type="password"
                            className="profile-input"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            required
                            autoComplete="new-password"
                        />
                        <PasswordStrengthMeter password={newPassword} />
                    </div>
                    <div className="profile-field">
                        <label htmlFor="confirmNewPwd">Confirm New Password:</label>
                        <input
                            id="confirmNewPwd"
                            type="password"
                            className="profile-input"
                            value={confirmNewPassword}
                            onChange={(e) => setConfirmNewPassword(e.target.value)}
                            required
                            autoComplete="new-password"
                        />
                    </div>
                    <button type="submit" disabled={changingPassword}>
                        {changingPassword ? 'Changing...' : 'Change Password'}
                    </button>
                </form>
            </div>

            <div className="profile-section">
                <h3>Two-factor authentication</h3>
                <div className="profile-field">
                    <label>Status:</label>
                    <span className="profile-mfa-status">
                        {enrolledFactors.length > 0 ? 'Enabled (Authenticator app)' : 'Not enabled'}
                    </span>
                </div>
                {enrolledFactors.length === 0 && (
                    <button type="button" onClick={() => navigate('/mfa-setup')}>
                        Enroll
                    </button>
                )}
                {enrolledFactors.length > 0 && mfaRequired && (
                    <div className="profile-field">
                        <span className="profile-mfa-note">Required for your role</span>
                    </div>
                )}
                {enrolledFactors.length > 0 && !mfaRequired && (
                    <button type="button" onClick={handleRemoveMfa} disabled={removingMfa}>
                        {removingMfa ? 'Removing...' : 'Remove'}
                    </button>
                )}
            </div>
        </div>
    );
};
